import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  Car,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorMessage } from '@/lib/api-client';
import { formatBRL, formatDate } from '@/lib/format';
import {
  useBillingSummary,
  useCancelSubscription,
  useCheckout,
  useInvoices,
  useSyncInvoices,
  type BillingSummary,
} from './api';
import {
  BILLING_TYPE_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANT,
  STATUS_LABELS,
  STATUS_VARIANT,
} from './types';

export function BillingPage() {
  const summaryQuery = useBillingSummary();
  const invoicesQuery = useInvoices({ page: 1, limit: 20 });
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const summary = summaryQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Cobrança</h1>
          <p className="text-sm text-muted-foreground">
            Quanto você paga, por quê, e o histórico de faturas.
          </p>
        </div>
        {summary && summary.status !== 'active' && summary.activeVehicles > 0 && (
          <Button onClick={() => setCheckoutOpen(true)}>
            <CreditCard className="mr-2 size-4" />
            {summary.status === 'cancelled' ? 'Reativar' : 'Pagar e liberar'}
          </Button>
        )}
      </div>

      {summaryQuery.isLoading && <Skeleton className="h-40 w-full" />}
      {summaryQuery.isError && (
        <Card className="border-destructive/40">
          <CardContent className="pt-6 text-sm text-destructive">
            Não foi possível carregar a cobrança: {extractErrorMessage(summaryQuery.error)}
          </CardContent>
        </Card>
      )}

      {summary && (
        <>
          <StatusAlert summary={summary} onCheckout={() => setCheckoutOpen(true)} />

          <div className="grid gap-4 md:grid-cols-3">
            <SubscriptionCard summary={summary} />
            <ProjectionCard summary={summary} />
            <NextInvoiceCard summary={summary} />
          </div>



          <InvoicesSection
            invoices={invoicesQuery.data?.data ?? []}
            total={invoicesQuery.data?.meta.total ?? 0}
            loading={invoicesQuery.isLoading}
            fake={summary.provider.fake}
          />

          <EncerrarAssinatura summary={summary} />

          <CheckoutDialog
            open={checkoutOpen}
            onOpenChange={setCheckoutOpen}
            summary={summary}
          />
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Aviso do topo — o que o usuário precisa fazer agora
// -----------------------------------------------------------------------------

function StatusAlert({
  summary,
  onCheckout,
}: {
  summary: BillingSummary;
  onCheckout: () => void;
}) {
  if (summary.status === 'active') return null;

  const config: Record<string, { tone: string; title: string; body: string }> = {
    pending_payment: {
      tone: 'border-warning/50 bg-warning/10 text-foreground',
      title:
        summary.activeVehicles === 0
          ? 'Cadastre seus veículos para começar'
          : 'Falta o primeiro pagamento',
      body:
        summary.activeVehicles === 0
          ? 'A mensalidade é calculada pelos veículos ativos. Cadastre a frota para ver o valor exato e liberar o sistema.'
          : `${summary.activeVehicles} veículo(s) cadastrado(s). Confirme o pagamento para liberar checklist, abastecimento, manutenção e multas.`,
    },
    past_due: {
      tone: 'border-warning/50 bg-warning/10 text-foreground',
      title: 'Pagamento pendente',
      body: 'Há fatura em aberto. O acesso continua liberado por alguns dias, e depois é suspenso automaticamente.',
    },
    suspended: {
      tone: 'border-destructive/50 bg-destructive/10 text-foreground',
      title: 'Acesso suspenso por falta de pagamento',
      body: `Suspenso em ${formatDate(summary.suspendedAt)}. Quitando a fatura em aberto, o acesso volta na hora.`,
    },
    cancelled: {
      tone: 'border-border bg-muted/40 text-foreground',
      title: 'Assinatura cancelada',
      body: 'Os dados continuam guardados. Contrate novamente quando quiser retomar.',
    },
  };

  const c = config[summary.status];
  if (!c) return null;

  return (
    <div className={`flex flex-wrap items-start gap-3 rounded-lg border p-4 ${c.tone}`}>
      <AlertTriangle className="mt-0.5 size-5 shrink-0" />
      <div className="min-w-56 flex-1">
        <p className="font-medium">{c.title}</p>
        <p className="text-sm text-muted-foreground">{c.body}</p>
      </div>
      {summary.activeVehicles === 0 ? (
        <Button size="sm" asChild>
          <Link to="/app/vehicles">Cadastrar veículos</Link>
        </Button>
      ) : (
        <Button size="sm" onClick={onCheckout}>
          {summary.status === 'cancelled' ? 'Reativar' : 'Pagar e liberar'}
        </Button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Cartões do topo
// -----------------------------------------------------------------------------

function SubscriptionCard({ summary }: { summary: BillingSummary }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardDescription>Situação</CardDescription>
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[summary.status]}>{STATUS_LABELS[summary.status]}</Badge>
          {summary.provider.fake && (
            <Badge variant="outline" title="Sem ASAAS_API_KEY: nenhuma cobrança real é gerada">
              simulado
            </Badge>
          )}
          {!summary.provider.fake && summary.provider.environment === 'sandbox' && (
            <Badge variant="outline">sandbox</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {summary.subscription ? (
          <>
            <p className="font-medium">
              {formatBRL(summary.subscription.valueCents)}/mês
            </p>
            <p className="text-muted-foreground">
              {BILLING_TYPE_LABELS[summary.subscription.billingType] ??
                summary.subscription.billingType}{' '}
              · desde {formatDate(summary.subscription.startedAt)}
            </p>
          </>
        ) : (
          <p className="text-muted-foreground">
            Nenhuma assinatura ativa. Tudo incluído, sem plano a escolher.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ProjectionCard({ summary }: { summary: BillingSummary }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardDescription>Valor do mês</CardDescription>
        <CardTitle className="font-mono text-3xl tabular-nums">
          {formatBRL(summary.projectedCents)}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
        <Car className="size-4" />
        {summary.activeVehicles} veículo(s) ativo(s) × {formatBRL(summary.pricePerVehicleCents)}
      </CardContent>
    </Card>
  );
}

function NextInvoiceCard({ summary }: { summary: BillingSummary }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardDescription>Próxima fatura</CardDescription>
        <CardTitle className="text-3xl">{formatDate(summary.nextDueDate)}</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
        <CalendarDays className="size-4" />
        em {summary.daysUntilNextInvoice} dia(s) · fecha todo dia {summary.billingDay}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Planos
// -----------------------------------------------------------------------------

/**
 * Encerrar a assinatura.
 *
 * Ficava dentro de uma seção "Planos" que listava um card só, marcado como
 * "atual" — uma escolha inexistente ocupando o meio da página. Com plano único
 * (ADR-0030), o que sobra de acionável ali é justamente isto, e o lugar dele é
 * discreto no fim, não em destaque.
 */
function EncerrarAssinatura({ summary }: { summary: BillingSummary }) {
  const cancelar = useCancelSubscription();

  if (!summary.subscription || summary.status === 'cancelled') return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
      <p className="text-sm text-muted-foreground">
        Ao cancelar, seus dados continuam guardados — você só deixa de ser cobrado.
      </p>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        disabled={cancelar.isPending}
        onClick={() =>
          cancelar.mutate(
            {},
            {
              onSuccess: () => toast.success('Assinatura cancelada'),
              onError: (err) => toast.error(extractErrorMessage(err)),
            },
          )
        }
      >
        Cancelar assinatura
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Faturas
// -----------------------------------------------------------------------------

function InvoicesSection({
  invoices,
  total,
  loading,
  fake,
}: {
  invoices: Array<{
    id: string;
    status: string;
    description: string | null;
    valueCents: number;
    billingType: string | null;
    dueDate: string;
    paidAt: string | null;
    invoiceUrl: string | null;
  }>;
  total: number;
  loading: boolean;
  fake: boolean;
}) {
  const sync = useSyncInvoices();

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Faturas</CardTitle>
          <CardDescription>
            {total === 0 ? 'Nenhuma fatura ainda' : `${total} fatura(s)`}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={fake || sync.isPending}
          title={fake ? 'Sem chave do Asaas configurada' : 'Repuxa as cobranças do Asaas'}
          onClick={() =>
            sync.mutate(undefined, {
              onSuccess: (r) => toast.success(`${r.synced} cobrança(s) sincronizada(s)`),
              onError: (err) => toast.error(extractErrorMessage(err)),
            })
          }
        >
          <RefreshCw className={`mr-2 size-4 ${sync.isPending ? 'animate-spin' : ''}`} />
          Sincronizar
        </Button>
      </CardHeader>
      <CardContent>
        {loading && <Skeleton className="h-24 w-full" />}

        {!loading && invoices.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
            <FileText className="size-8 opacity-40" />
            <p>As faturas aparecem aqui assim que a assinatura gerar a primeira cobrança.</p>
          </div>
        )}

        {!loading && invoices.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Vencimento</th>
                  <th className="pb-2 pr-4 font-medium">Descrição</th>
                  <th className="pb-2 pr-4 font-medium">Forma</th>
                  <th className="pb-2 pr-4 text-right font-medium">Valor</th>
                  <th className="pb-2 pr-4 font-medium">Situação</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-border/60 last:border-0">
                    <td className="py-3 pr-4 whitespace-nowrap">{formatDate(invoice.dueDate)}</td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {invoice.description ?? '—'}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">
                      {invoice.billingType
                        ? (BILLING_TYPE_LABELS[invoice.billingType] ?? invoice.billingType)
                        : '—'}
                    </td>
                    <td className="py-3 pr-4 text-right font-mono tabular-nums">
                      {formatBRL(invoice.valueCents)}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={INVOICE_STATUS_VARIANT[invoice.status] ?? 'secondary'}>
                          {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
                        </Badge>
                        {invoice.paidAt && (
                          <span className="whitespace-nowrap text-xs text-muted-foreground">
                            em {formatDate(invoice.paidAt)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      {invoice.invoiceUrl && (
                        <a
                          href={invoice.invoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          Abrir
                          <ExternalLink className="size-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Contratação
// -----------------------------------------------------------------------------

const BILLING_TYPE_OPTIONS = ['UNDEFINED', 'BOLETO', 'PIX', 'CREDIT_CARD'] as const;

function CheckoutDialog({
  open,
  onOpenChange,
  summary,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: BillingSummary;
}) {
  const checkout = useCheckout();
  const [billingType, setBillingType] = useState<string>('UNDEFINED');
  const [planCode, setPlanCode] = useState<string>(summary.plan.code);

  const selectedPlan = summary.availablePlans.find((p) => p.code === planCode);
  const overCapacity =
    selectedPlan?.maxVehicles != null && summary.activeVehicles > selectedPlan.maxVehicles;
  const noVehicles = summary.activeVehicles === 0;

  const submit = () => {
    checkout.mutate(
      { billingType, planCode },
      {
        onSuccess: () => {
          toast.success('Assinatura contratada');
          onOpenChange(false);
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contratar assinatura</DialogTitle>
          <DialogDescription>
            A cobrança é mensal e proporcional à frota: {summary.activeVehicles} veículo(s) ativo(s)
            × {formatBRL(summary.pricePerVehicleCents)}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Plano</label>
            <Select value={planCode} onValueChange={setPlanCode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {summary.availablePlans.map((plan) => (
                  <SelectItem key={plan.code} value={plan.code}>
                    {plan.name}
                    {plan.maxVehicles != null ? ` — até ${plan.maxVehicles} veículos` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Forma de pagamento</label>
            <Select value={billingType} onValueChange={setBillingType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BILLING_TYPE_OPTIONS.map((type) => (
                  <SelectItem key={type} value={type}>
                    {BILLING_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total mensal</span>
              <span className="font-mono text-xl font-semibold tabular-nums">
                {formatBRL(summary.projectedCents)}
              </span>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="size-3.5" />
              Primeira cobrança em {formatDate(summary.nextDueDate)}
            </p>
          </div>

          {noVehicles && (
            <p className="text-sm text-destructive">
              Cadastre ao menos um veículo ativo antes de contratar — o valor é por veículo.
            </p>
          )}
          {overCapacity && (
            <p className="text-sm text-destructive">
              A frota tem {summary.activeVehicles} veículos ativos e o plano {selectedPlan?.name}{' '}
              permite {selectedPlan?.maxVehicles}.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={checkout.isPending || noVehicles || overCapacity}>
            {checkout.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Confirmar contratação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
